import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { IoChatbubbleEllipsesOutline, IoArrowForward, IoTrashOutline, IoMegaphoneOutline } from "react-icons/io5";
import api from "@/api";
import { useI18n, STORE_LABELS } from "@/i18n";
import { useAuth } from "@/auth";
import { AppHeader } from "@/components/AppHeader";
import { BottomNav } from "@/components/BottomNav";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StoreAvatar, StorePairAvatar } from "@/components/StoreAvatar";
import { useStoreImages } from "@/hooks/useStoreImages";
import { PullToRefresh } from "@/components/PullToRefresh";
import { relativeTime } from "@/utils";

export default function ChatListPage() {
  const { t, lang } = useI18n();
  const { user, isTitolare } = useAuth();
  const navigate = useNavigate();
  const storeImages = useStoreImages();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        api.get("/chat/conversations"),
        api.get("/chat/unread").catch(() => ({ data: { per_conversation: [] } })),
      ]);
      setConvs(c.data);
      const map = {};
      (u.data?.per_conversation || []).forEach((x) => { map[x.conv_id] = x.count; });
      setUnread(map);
    } catch {
      toast.error("Errore caricamento chat");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/chat/conversations/${pendingDelete}`);
      toast.success("Chat eliminata");
      setPendingDelete(null);
      load();
      window.dispatchEvent(new CustomEvent("chat-unread-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="mobile-frame with-bottom-bar" data-testid="chat-list-page">
      <AppHeader title={t("chat")} showBack backTo="/" />
      <PullToRefresh onRefresh={load}>
      <div className="tf-content">
        {loading && <div className="tf-empty">{t("loading")}</div>}
        {!loading && convs.length === 0 && (
          <div className="tf-empty">
            <IoChatbubbleEllipsesOutline size={44} style={{ opacity: 0.4 }} />
            <p>Nessuna chat</p>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {convs.map((c) => {
            const isBroadcast = c.is_broadcast || c.conv_id === "broadcast";
            const other = user?.storeId && c.store_a === user.storeId ? c.store_b : c.store_a;
            let title;
            if (isBroadcast) title = "Tutti i negozi";
            else title = isTitolare
              ? `${t(STORE_LABELS[c.store_a])} · ${t(STORE_LABELS[c.store_b])}`
              : t(STORE_LABELS[other]);
            const unreadCount = unread[c.conv_id] || 0;
            return (
              <div key={c.conv_id} className="tf-chat-row-wrap">
                <button
                  data-testid={`chat-item-${c.conv_id}`}
                  className="tf-chat-item"
                  onClick={() => navigate(`/chat/${c.conv_id}`)}
                >
                  <div className="tf-chat-item-icon" style={{ background: "transparent", padding: 0 }}>
                    {isBroadcast ? (
                      <span
                        className="tf-store-avatar"
                        style={{ width: 44, height: 44, background: "linear-gradient(135deg,#E2001A 0%,#F59E0B 100%)", color: "#fff", boxShadow: "0 0 0 3px rgba(226,0,26,0.25)" }}
                        aria-label="broadcast"
                      >
                        <IoMegaphoneOutline size={22} />
                      </span>
                    ) : isTitolare ? (
                      <StorePairAvatar storeA={c.store_a} storeB={c.store_b} size={44} imageUrls={storeImages} />
                    ) : (
                      <StoreAvatar storeId={other} size={44} imageUrl={storeImages[other]} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="tf-chat-item-title">
                      {title}
                      {unreadCount > 0 && (
                        <span className="tf-chat-unread-badge" data-testid={`chat-unread-${c.conv_id}`}>{unreadCount > 9 ? "9+" : unreadCount}</span>
                      )}
                    </div>
                    <div className="tf-chat-item-sub">
                      {c.last_message ? (c.last_message.text || (c.last_message.attachment_type === "image" ? "📷 Foto" : c.last_message.attachment_type === "audio" ? "🎤 Audio" : "")).split("\n")[0].slice(0, 60) : "Nessun messaggio"}
                    </div>
                  </div>
                  {c.last_message && (
                    <div className="tf-chat-item-time">{relativeTime(c.last_message.created_at, lang)}</div>
                  )}
                  <IoArrowForward size={18} color="var(--tf-muted)" />
                </button>
                {isTitolare && (
                  <button
                    type="button"
                    className="tf-chat-delete-btn"
                    data-testid={`chat-delete-${c.conv_id}`}
                    onClick={(e) => { e.stopPropagation(); setPendingDelete(c.conv_id); }}
                    aria-label="Elimina chat"
                    title="Elimina chat"
                  >
                    <IoTrashOutline size={18} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </PullToRefresh>
      <BottomNav />
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(v) => !v && setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Eliminare la chat?"
        description="Tutti i messaggi verranno cancellati definitivamente."
        confirmLabel="Elimina"
      />
    </div>
  );
}
